/**
 * DeviceBridgeService — Local-First WebSocket Connection to Bridge App
 *
 * Connection priority (per C-3147 architecture):
 *   1. ws://localhost:8765  — direct to local bridge app (low latency, no backend dependency)
 *   2. wss://backend/lis/bridge/ws/frontend — backend relay (fallback for remote users)
 *
 * The service tries the local bridge first on every connection attempt.
 * If local is unavailable, it falls back to the backend relay.
 * It periodically re-checks local availability even when connected via backend.
 */

const DEFAULT_LOCAL_BRIDGE_PORT = 8765;
const LOCAL_PORT_STORAGE_KEY = "bridge-local-port";
const LOCAL_RECONNECT_DELAY_MS = 5000;
const BACKEND_RECONNECT_DELAY_MS = 10000;
const LOCAL_RECHECK_INTERVAL_MS = 10000; // re-try local every 10s when on backend
const LOCAL_CONNECT_TIMEOUT_MS = 2000; // fast fail for local connection

export type ConnectionMode = "local" | "backend" | "disconnected";

export interface BridgeMessage {
  type: string;
  requestId?: string;
  payload?: Record<string, unknown>;
}

export interface BridgeResponse {
  type: string;
  requestId?: string;
  payload?: Record<string, unknown>;
}

type MessageHandler = (message: BridgeResponse) => void;
type ConnectionHandler = (mode: ConnectionMode) => void;

class DeviceBridgeService {
  private ws: WebSocket | null = null;

  private connectionMode: ConnectionMode = "disconnected";

  private messageHandlers: Set<MessageHandler> = new Set();

  private connectionHandlers: Set<ConnectionHandler> = new Set();

  private pendingRequests: Map<
    string,
    {
      resolve: (value: BridgeResponse) => void;
      reject: (reason: Error) => void;
      timeout: ReturnType<typeof setTimeout>;
    }
  > = new Map();

  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

  private localRecheckInterval: ReturnType<typeof setInterval> | null = null;

  private isConnecting = false;

  private destroyed = false;

  private refCount = 0;

  private localPort: number = DEFAULT_LOCAL_BRIDGE_PORT;

  constructor() {
    // Restore persisted port so page refresh uses the right port
    try {
      const stored = localStorage.getItem(LOCAL_PORT_STORAGE_KEY);
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (parsed > 0) this.localPort = parsed;
      }
    } catch {
      // localStorage unavailable
    }
  }

  /**
   * Set the local bridge port (read from the selected workstation's localPort preference).
   * Persists to localStorage so it survives page refreshes.
   */
  setLocalPort(port: number): void {
    this.localPort = port > 0 ? port : DEFAULT_LOCAL_BRIDGE_PORT;
    try {
      localStorage.setItem(LOCAL_PORT_STORAGE_KEY, String(this.localPort));
    } catch {
      // localStorage unavailable
    }
  }

  private getLocalBridgeUrl(): string {
    return `ws://localhost:${this.localPort}`;
  }

  /**
   * Start the connection lifecycle. Uses reference counting so the
   * connection stays alive until all consumers have called disconnect().
   */
  connect(): void {
    this.refCount += 1;
    if (this.refCount === 1) {
      this.destroyed = false;
      this.tryLocalConnection();
    }
  }

  /**
   * Decrement the reference count. Only truly disconnects when it reaches 0.
   */
  disconnect(): void {
    this.refCount = Math.max(0, this.refCount - 1);
    if (this.refCount === 0) {
      this.destroyConnection();
    }
  }

  /**
   * Force a full reconnect regardless of reference count.
   * Used when the local bridge port changes (e.g. workstation switch).
   */
  forceReconnect(): void {
    if (this.refCount <= 0) return; // no active consumers
    this.destroyConnection();
    this.destroyed = false;
    this.tryLocalConnection();
  }

  private destroyConnection(): void {
    this.destroyed = true;
    this.clearTimers();
    if (this.ws) {
      this.ws.onclose = null; // prevent reconnect on intentional close
      this.ws.close();
      this.ws = null;
    }
    this.setConnectionMode("disconnected");
  }

  /**
   * Get the current connection mode.
   */
  getConnectionMode(): ConnectionMode {
    return this.connectionMode;
  }

  /**
   * Whether the service has an active connection (local or backend).
   */
  isConnected(): boolean {
    return this.connectionMode !== "disconnected";
  }

  /**
   * Whether the service is connected directly to the local bridge.
   */
  isLocal(): boolean {
    return this.connectionMode === "local";
  }

  /**
   * Send a message to the bridge and wait for a response matching the requestId.
   */
  async sendCommand(
    message: BridgeMessage,
    timeoutMs = 30000
  ): Promise<BridgeResponse> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error("Bridge not connected");
    }

    const requestId = message.requestId || crypto.randomUUID();
    const msgWithId = { ...message, requestId };

    return new Promise<BridgeResponse>((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.pendingRequests.delete(requestId);
        reject(new Error(`Bridge command timed out after ${timeoutMs}ms`));
      }, timeoutMs);

      this.pendingRequests.set(requestId, { resolve, reject, timeout });
      this.ws!.send(JSON.stringify(msgWithId));
    });
  }

  /**
   * Send a fire-and-forget message (no response expected).
   */
  send(message: BridgeMessage): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error("Bridge not connected");
    }
    this.ws.send(JSON.stringify(message));
  }

  /**
   * Subscribe to all incoming bridge messages.
   */
  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    return () => this.messageHandlers.delete(handler);
  }

  /**
   * Subscribe to connection mode changes.
   */
  onConnectionChange(handler: ConnectionHandler): () => void {
    this.connectionHandlers.add(handler);
    return () => this.connectionHandlers.delete(handler);
  }

  // ── Private ──────────────────────────────────────────────────────────

  private tryLocalConnection(): void {
    if (this.destroyed || this.isConnecting) return;
    this.isConnecting = true;

    const ws = new WebSocket(this.getLocalBridgeUrl());
    let timedOut = false;

    // Fast timeout — if local bridge isn't there, fail quickly
    const connectTimeout = setTimeout(() => {
      timedOut = true;
      ws.close();
      this.isConnecting = false;
      this.fallbackToBackend();
    }, LOCAL_CONNECT_TIMEOUT_MS);

    ws.onopen = () => {
      if (timedOut) return;
      clearTimeout(connectTimeout);
      this.isConnecting = false;
      this.attachSocket(ws, "local");
    };

    ws.onerror = () => {
      if (timedOut) return;
      clearTimeout(connectTimeout);
      ws.close();
    };

    ws.onclose = () => {
      if (timedOut) return;
      clearTimeout(connectTimeout);
      this.isConnecting = false;
      this.fallbackToBackend();
    };
  }

  private fallbackToBackend(): void {
    if (this.destroyed) return;

    const token =
      localStorage.getItem("token") || localStorage.getItem("lis_token");
    if (!token) {
      // No auth token — retry local after delay
      this.scheduleReconnect(LOCAL_RECONNECT_DELAY_MS);
      return;
    }

    this.isConnecting = true;
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const host = window.location.host;
    const url = `${protocol}//${host}/lis/bridge/ws/frontend?token=${token}`;

    const ws = new WebSocket(url);

    ws.onopen = () => {
      this.isConnecting = false;
      this.attachSocket(ws, "backend");
      // When connected via backend, periodically try to upgrade to local
      this.startLocalRecheck();
    };

    ws.onerror = () => {
      ws.close();
    };

    ws.onclose = () => {
      this.isConnecting = false;
      this.setConnectionMode("disconnected");
      this.scheduleReconnect(BACKEND_RECONNECT_DELAY_MS);
    };
  }

  private attachSocket(ws: WebSocket, mode: ConnectionMode): void {
    // Close existing connection if switching
    if (this.ws && this.ws !== ws) {
      const oldWs = this.ws;
      oldWs.onclose = null;
      oldWs.close();
    }

    this.ws = ws;
    this.setConnectionMode(mode);
    this.stopLocalRecheck(); // will restart if needed

    ws.onmessage = (event: MessageEvent) => {
      try {
        const data: BridgeResponse = JSON.parse(event.data);
        // Resolve pending request if there's a matching requestId
        if (data.requestId && this.pendingRequests.has(data.requestId)) {
          const pending = this.pendingRequests.get(data.requestId)!;
          clearTimeout(pending.timeout);
          this.pendingRequests.delete(data.requestId);
          pending.resolve(data);
        }
        // Broadcast to all message handlers regardless
        this.messageHandlers.forEach((handler) => handler(data));
      } catch {
        // ignore parse errors
      }
    };

    ws.onclose = () => {
      this.ws = null;
      this.setConnectionMode("disconnected");
      this.rejectAllPending("Connection closed");
      // Always try local first on reconnect
      this.scheduleReconnect(LOCAL_RECONNECT_DELAY_MS);
    };

    ws.onerror = () => {
      // onclose will fire after onerror
    };
  }

  private setConnectionMode(mode: ConnectionMode): void {
    if (this.connectionMode !== mode) {
      this.connectionMode = mode;
      this.connectionHandlers.forEach((handler) => handler(mode));
    }
  }

  private scheduleReconnect(delayMs: number): void {
    if (this.destroyed) return;
    this.clearTimers();
    this.reconnectTimeout = setTimeout(() => {
      this.tryLocalConnection(); // always try local first
    }, delayMs);
  }

  /**
   * When connected via backend, periodically attempt to upgrade to local.
   * If local connection succeeds, the backend connection is replaced.
   */
  private startLocalRecheck(): void {
    this.stopLocalRecheck();
    this.localRecheckInterval = setInterval(() => {
      if (this.connectionMode !== "backend" || this.isConnecting) return;
      this.tryUpgradeToLocal();
    }, LOCAL_RECHECK_INTERVAL_MS);
  }

  private stopLocalRecheck(): void {
    if (this.localRecheckInterval) {
      clearInterval(this.localRecheckInterval);
      this.localRecheckInterval = null;
    }
  }

  /**
   * Try to connect to local bridge while already connected via backend.
   * If successful, replace the backend connection with the local one.
   */
  private tryUpgradeToLocal(): void {
    const ws = new WebSocket(this.getLocalBridgeUrl());
    let timedOut = false;

    const connectTimeout = setTimeout(() => {
      timedOut = true;
      ws.close();
    }, LOCAL_CONNECT_TIMEOUT_MS);

    ws.onopen = () => {
      if (timedOut) return;
      clearTimeout(connectTimeout);
      // Local bridge is now available — switch to it
      this.attachSocket(ws, "local");
    };

    ws.onerror = () => {
      if (timedOut) return;
      clearTimeout(connectTimeout);
      ws.close();
      // Stay on backend — local not available yet
    };

    ws.onclose = () => {
      // noop — stay on backend
    };
  }

  private rejectAllPending(reason: string): void {
    this.pendingRequests.forEach((pending) => {
      clearTimeout(pending.timeout);
      pending.reject(new Error(reason));
    });
    this.pendingRequests.clear();
  }

  private clearTimers(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    this.stopLocalRecheck();
  }
}

/** Singleton instance */
export const deviceBridgeService = new DeviceBridgeService();
